import { AxiosError } from "axios";
import { Button } from "@mui/material";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";

import { ApiError, InventoryItem, UploadResult } from "../../models";
import { uploadFile } from "../../api/generic-api";

interface UseUploadInventoryItemsOptions {
  onUploadResult?: (result: UploadResult<InventoryItem>) => void;
  onOpenDetails?: () => void;
}

export const useUploadInventoryItems = (
  options?: UseUploadInventoryItemsOptions
) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  return useMutation<UploadResult<InventoryItem>, AxiosError<ApiError>, File>(
    (file: File) =>
      uploadFile<{ file: File }, UploadResult<InventoryItem>>(
        `/inventory-items/upload`,
        { file }
      ),
    {
      onSuccess: (result) => {
        options?.onUploadResult?.(result);

        const { createdCount, updatedCount, skippedCount, rowErrors } = result;
        const errorCount = rowErrors?.length ?? 0;
        const summary = `Upload complete: ${createdCount} created, ${updatedCount} updated, ${skippedCount} skipped${
          errorCount ? `, ${errorCount} with issues` : ""
        }.`;

        enqueueSnackbar(summary, {
          variant: errorCount ? "warning" : "success",
          action:
            options?.onOpenDetails &&
            ((key) => (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  options.onOpenDetails?.();
                  closeSnackbar(key);
                }}
              >
                View details
              </Button>
            )),
        });
      },
      onError: (error) => {
        const message =
          error.response?.data?.message ?? "Inventory Items Uploading Error";
        enqueueSnackbar(message, { variant: "error" });
      },
    }
  );
};
